import React, { useState, useEffect } from 'react';
import {
    TextField, Button, Grid, MenuItem, Paper, Typography,
    CircularProgress, Alert, Box, Container, Checkbox,
    ListItemText, Chip, FormControl,
    Accordion, AccordionSummary, AccordionDetails, FormControlLabel, Switch,
    Divider, Tooltip, IconButton, List, ListItem, ListItemButton, Avatar,
    Autocomplete,
    createFilterOptions,
    FilterOptionsState
} from '@mui/material';
import {
    ExpandMore as ExpandMoreIcon,
    DirectionsCar as DriverIcon,
    Refresh as RecoveryIcon,
    Person as PersonIcon,
    TimerOff as ZeroHoursIcon,
    CarRental as WeekDriverIcon,
    Close,
    CheckBoxOutlineBlank,
    CheckBox,
    Work,
    StarOutline,
    PostAdd,
    Apartment
} from '@mui/icons-material';
import toast from 'react-hot-toast';
import { useAuth } from '../context/authContext';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../redux/redux.hook';
import { addOfflineTimesheetOperation, createTimesheet } from '../redux/timeSheet.slice';
import { DayBonus, DayDetails, Employee, Foreman, SiteDetail, Timesheet, WorkManager } from '../interfaces/timesheet';
import { getCurrentWeekNumber } from '../shared/utils';
import { addDays, format, getISOWeek, startOfWeek } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useUserRole } from '../context/userRoleContext';
import { setTeamForSite } from '../redux/team.slice';
import { WorkerSettings } from '../interfaces/timesheet';
import { fetchData } from '../redux/data.slice';
import BonusDialog from '../components/BonusDialog';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import { DateTime } from 'luxon';

type DayOfWeek = 'lundi' | 'mardi' | 'mercredi' | 'jeudi' | 'vendredi' | 'samedi' | 'dimanche';
const WEEKDAYS: DayOfWeek[] = ['lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi', 'dimanche'];

const toLocalIsoDate = (date: Date): string => format(date, 'yyyy-MM-dd');

// Default day details
const getDefaultDayDetails = () => ({
    hours: 8,
    quarters: 0,
    isDriver: false,
    isRecovery: false,
    bonuses: []
});

// Default day details for all days
const getDefaultDayDetailsObject = () => ({
    lundi: getDefaultDayDetails(),
    mardi: getDefaultDayDetails(),
    mercredi: getDefaultDayDetails(),
    jeudi: getDefaultDayDetails(),
    vendredi: getDefaultDayDetails(),
    samedi: { ...getDefaultDayDetails(), hours: 0 },
    dimanche: { ...getDefaultDayDetails(), hours: 0 }
});

const createMultiFieldFilter = () => {
    const defaultFilterOptions = createFilterOptions();

    return (options: any[], state: FilterOptionsState<unknown>) => {
        if (!state.inputValue) {
            return defaultFilterOptions(options, state);
        }

        const searchText = state.inputValue.toLowerCase();
        return options.filter(option =>
            option.name.toLowerCase().includes(searchText) ||
            option.matricule.toString().toLowerCase().includes(searchText)
        );
    };
};

export const TeamWorkForm: React.FC = () => {
    const { isAuthenticated, account } = useAuth();
    const navigation = useNavigate();
    const dispatch = useAppDispatch();
    const { isOnline } = useAppSelector(state => state.sync);
    const [isloading, setIsLoading] = useState(false);
    const { userRole } = useUserRole();
    const teamMembers = useAppSelector(state => state.team.teamsBySite);

    // Common form fields
    const [formCommon, setFormCommon] = useState<{
        chefChantier: WorkManager | null;
        chefProjet: Foreman | null;
        weekNumber: number;
        timesheetDate: string;
        worksite: SiteDetail | null;
        remark: string | null;
    }>({
        chefChantier: null,
        chefProjet: null,
        weekNumber: getCurrentWeekNumber(new Date()),
        timesheetDate: toLocalIsoDate(new Date()),
        worksite: null,
        remark: null
    });

    // State for selected workers
    const [selectedWorkers, setSelectedWorkers] = useState<Employee[]>([]);

    // State for worker-specific settings
    const [workerSettings, setWorkerSettings] = useState<WorkerSettings>({});

    // Currently selected worker for detailed view
    const [currentWorker, setCurrentWorker] = useState<Employee | null>(null);

    // State for expanded day settings
    const [expandedDay, setExpandedDay] = useState<string | false>(false);

    // Maximum hours that can be selected
    const MAX_HOURS = 12;
    const hoursOptions = Array.from({ length: MAX_HOURS + 1 }, (_, i) => i);
    // Quarter hour options
    const quarterOptions = [0, 0.25, 0.50, 0.75];

    // Form validation
    const isFormValid = formCommon.chefChantier &&
        formCommon.chefProjet &&
        formCommon.worksite &&
        selectedWorkers.length > 0 && formCommon.timesheetDate && formCommon.weekNumber > 0;

    const { data, loading, error } = useAppSelector(state => state.data);
    const multiFieldFilter = createMultiFieldFilter();
    const weekdays = [
        { key: 'lundi', label: 'Lundi' },
        { key: 'mardi', label: 'Mardi' },
        { key: 'mercredi', label: 'Mercredi' },
        { key: 'jeudi', label: 'Jeudi' },
        { key: 'vendredi', label: 'Vendredi' },
        { key: 'samedi', label: 'Samedi' },
        { key: 'dimanche', label: 'Dimanche' }
    ];

    const [bonusDialogOpen, setBonusDialogOpen] = useState(false);
    const [currentBonusDay, setCurrentBonusDay] = useState<{ worker: Employee | null; day: DayOfWeek | null }>({
        worker: null,
        day: null
    });

    const handleOpenBonusDialog = (worker: Employee, day: DayOfWeek) => {
        setCurrentBonusDay({ worker, day });
        setBonusDialogOpen(true);
    };

    // Handler to save bonuses
    const handleSaveBonuses = (bonuses: DayBonus[]) => {
        if (!currentBonusDay.worker || !currentBonusDay.day) return;

        const worker = currentBonusDay.worker;
        const day = currentBonusDay.day;

        // Create a copy of the worker settings to ensure immutability
        const updatedWorkerSettings = {
            ...workerSettings,
            [worker.matricule]: {
                ...workerSettings[worker.matricule],
                dayDetails: {
                    ...workerSettings[worker.matricule].dayDetails,
                    [day]: {
                        ...workerSettings[worker.matricule].dayDetails[day],
                        bonuses: [...bonuses] // Create a new array to ensure state change is detected
                    }
                }
            }
        };

        setWorkerSettings(updatedWorkerSettings);
    };

    useEffect(() => {
        if (data?.workManagers && account?.username) {
            // Find the work manager that matches the current user's email
            const currentUserAsManager = data.workManagers.find(
                manager => manager.email.toLowerCase() === account?.username.toLowerCase()
            );

            if (currentUserAsManager) {
                // Auto-set the chef de chantier field
                setFormCommon(prev => ({
                    ...prev,
                    chefChantier: currentUserAsManager
                }));
            }
        }
    }, [data?.workManagers, account?.username]);

    const fetch = async () => {
        if (isAuthenticated) {
            await dispatch(fetchData()).unwrap();
        }
    }

    useEffect(() => {
        fetch()
    }, []);

    // When workers selection changes, update currentWorker if needed
    useEffect(() => {
        // Set first worker as current if current is null or not in selection
        if (
            selectedWorkers.length > 0 &&
            (currentWorker === null || !selectedWorkers.some(w => w.matricule === currentWorker.matricule))
        ) {
            setCurrentWorker(selectedWorkers[0]);
        } else if (selectedWorkers.length === 0) {
            setCurrentWorker(null);
        }

        // Clean up worker settings for removed workers
        if (Object.keys(workerSettings).length > 0) {
            const newSettings = { ...workerSettings };
            let hasChanges = false;

            Object.keys(newSettings).forEach(workerMatricule => {
                if (!selectedWorkers.some(w => w.matricule === parseInt(workerMatricule, 10))) {
                    delete newSettings[workerMatricule];
                    hasChanges = true;
                }
            });

            if (hasChanges) {
                setWorkerSettings(newSettings);
            }
        }

        // Initialize settings for new workers
        selectedWorkers.forEach(worker => {
            if (!workerSettings[worker.matricule]) {
                setWorkerSettings(prev => ({
                    ...prev,
                    [worker.matricule]: {
                        dayDetails: getDefaultDayDetailsObject(),
                        isWeekDriver: false,
                        isWeekRecovery: false,
                        remark: null
                    }
                }));
            }
        });
    }, [selectedWorkers]);

    useEffect(() => {
        if (formCommon.worksite) {
            const worksiteMatricule = formCommon.worksite.matricule;
            const teamForWorksite = teamMembers[worksiteMatricule] || [];

            // Update selected workers when worksite changes
            setSelectedWorkers(teamForWorksite);

            // Initialize worker settings for the selected workers if necessary
            setWorkerSettings(prevSettings => {
                const newSettings = { ...prevSettings };
                teamForWorksite.forEach(worker => {
                    if (!newSettings[worker.matricule]) {
                        newSettings[worker.matricule] = {
                            dayDetails: getDefaultDayDetailsObject(),
                            isWeekDriver: false,
                            isWeekRecovery: false,
                            remark: null
                        };
                    }
                });
                return newSettings;
            });
        }
    }, [formCommon.worksite, teamMembers]);

    // Reset all hours to zero for specific worker
    const handleResetWorkerHoursToZero = (worker: Employee) => {
        const updatedDayDetails = { ...workerSettings[worker.matricule].dayDetails };

        WEEKDAYS.forEach(day => {
            updatedDayDetails[day] = {
                ...updatedDayDetails[day],
                hours: 0
            };
        });

        setWorkerSettings({
            ...workerSettings,
            [worker.matricule]: {
                ...workerSettings[worker.matricule],
                dayDetails: updatedDayDetails
            }
        });
    };

    // Handle week driver status for a worker
    const handleWorkerWeekDriver = (worker: Employee, isWeekDriver: boolean) => {
        const updatedDayDetails = { ...workerSettings[worker.matricule].dayDetails };

        // Update the isDriver status for all workdays
        ['lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi'].forEach(day => {
            updatedDayDetails[day as DayOfWeek] = {
                ...updatedDayDetails[day as DayOfWeek],
                isDriver: isWeekDriver // Set to match the toggle state
            };
        });

        setWorkerSettings({
            ...workerSettings,
            [worker.matricule]: {
                ...workerSettings[worker.matricule],
                dayDetails: updatedDayDetails,
                isWeekDriver: isWeekDriver
            }
        });
    };

    // Handle week recovery change
    const handleWeekRecoveryChange = (worker: Employee, isWeekRecovery: boolean) => {
        const updatedDayDetails = { ...workerSettings[worker.matricule].dayDetails };

        // Update the isRecovery status for all workdays
        ['lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi'].forEach(day => {
            updatedDayDetails[day as DayOfWeek] = {
                ...updatedDayDetails[day as DayOfWeek],
                isRecovery: isWeekRecovery // Set to match the toggle state
            };
        });

        setWorkerSettings({
            ...workerSettings,
            [worker.matricule]: {
                ...workerSettings[worker.matricule],
                dayDetails: updatedDayDetails,
                isWeekRecovery: isWeekRecovery
            }
        });
    };

    // Handle hours change for a worker
    const handleWorkerHoursChange = (worker: Employee, day: DayOfWeek, value: number) => {
        setWorkerSettings({
            ...workerSettings,
            [worker.matricule]: {
                ...workerSettings[worker.matricule],
                dayDetails: {
                    ...workerSettings[worker.matricule].dayDetails,
                    [day]: {
                        ...workerSettings[worker.matricule].dayDetails[day],
                        hours: value
                    }
                }
            }
        });
    };

    // Handle worker-specific quarter change
    const handleWorkerQuarterChange = (worker: Employee, day: DayOfWeek, value: number) => {
        setWorkerSettings({
            ...workerSettings,
            [worker.matricule]: {
                ...workerSettings[worker.matricule],
                dayDetails: {
                    ...workerSettings[worker.matricule].dayDetails,
                    [day]: {
                        ...workerSettings[worker.matricule].dayDetails[day],
                        quarters: value
                    }
                }
            }
        });
    };

    // Handle worker-specific driver change
    const handleWorkerDriverChange = (worker: Employee, day: DayOfWeek, checked: boolean) => {
        // Update the current day's driver status
        const updatedDayDetails = {
            ...workerSettings[worker.matricule].dayDetails,
            [day]: {
                ...workerSettings[worker.matricule].dayDetails[day],
                isDriver: checked
            }
        };

        // Check if all weekdays are now set as driver days
        const allWeekdaysAreDriverDays = ['lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi'].every(
            weekday => {
                // For the current day being changed, use the new value
                if (weekday === day) return checked;
                // For other days, check their current values
                return updatedDayDetails[weekday as DayOfWeek].isDriver;
            }
        );

        // Update worker settings with new day details and potentially updated week driver status
        setWorkerSettings({
            ...workerSettings,
            [worker.matricule]: {
                ...workerSettings[worker.matricule],
                dayDetails: updatedDayDetails,
                // Update isWeekDriver if all weekdays are now driver days
                isWeekDriver: allWeekdaysAreDriverDays
            }
        });
    };

    // Handle worker-specific recovery change
    const handleWorkerRecoveryChange = (worker: Employee, day: DayOfWeek, checked: boolean) => {
        // Update the current day's recovery status
        const updatedDayDetails = {
            ...workerSettings[worker.matricule].dayDetails,
            [day]: {
                ...workerSettings[worker.matricule].dayDetails[day],
                isRecovery: checked
            }
        };

        // Check if all weekdays are now set as recovery days
        const allWeekdaysAreRecoveryDays = ['lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi'].every(
            weekday => {
                // For the current day being changed, use the new value
                if (weekday === day) return checked;
                // For other days, check their current values
                return updatedDayDetails[weekday as DayOfWeek].isRecovery;
            }
        );

        // Update worker settings with new day details and potentially updated week recovery status
        setWorkerSettings({
            ...workerSettings,
            [worker.matricule]: {
                ...workerSettings[worker.matricule],
                dayDetails: updatedDayDetails,
                // Update isWeekRecovery if all weekdays are now recovery days
                isWeekRecovery: allWeekdaysAreRecoveryDays
            }
        });
    };

    // For removing a single worker from selection
    const handleRemoveWorker = (worker: Employee) => {
        setSelectedWorkers(selectedWorkers.filter(w => w.matricule !== worker.matricule));
    };

    // Handle accordion expansion
    const handleAccordionChange = (day: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
        setExpandedDay(isExpanded ? day : false);
    };

    const handleSelectWorker = (worker: Employee) => {
        setCurrentWorker(worker);
    };

    const hasEffectiveBonus = (bonuses?: DayBonus[]): boolean => {
        if (!Array.isArray(bonuses) || bonuses.length === 0) return false;
        // consider a bonus “assigned” only if it carries >0 hours
        return bonuses.some(b => (Number(b?.hours) ?? 0) > 0);
    }

    const isDayEmpty = (day?: Partial<DayDetails>): boolean => {
        if (!day) return true; // missing -> treat as empty
        const hours = Number(day.hours ?? 0) + Number(day.quarters ?? 0);
        return hours <= 0 && !hasEffectiveBonus(day.bonuses);
    }

    const isWeekEmpty = (dayDetails: Record<string, DayDetails> | undefined): boolean => {
        if (!dayDetails) return true;
        return WEEKDAYS.every(d => isDayEmpty(dayDetails[d]));
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);
        if (!isFormValid) {
            toast.error('Veuillez remplir tous les champs obligatoires et sélectionner au moins un travailleur');
            setIsLoading(false);
            return;
        }

        try {
            let successCount = 0;

            // Create a timesheet for each selected worker
            for (const worker of selectedWorkers) {
                // Get worker-specific settings
                const workerData = workerSettings[worker.matricule];

                const timesheetData: Timesheet = {
                    chefChantier: formCommon.chefChantier!,
                    chefProjet: formCommon.chefProjet!,
                    travailleur: worker,
                    dayDetails: workerData.dayDetails,
                    foreman_validated: false,
                    hr_validated: false,
                    weekNumber: formCommon.weekNumber,
                    timesheetDate: formCommon.timesheetDate,
                    worksite: formCommon.worksite!,
                    remark: workerData.remark || "",
                    isInterim: worker && worker.isInterim ? worker.isInterim : false,
                    interimCompanyName: worker && worker.interimCompanyName ? worker.interimCompanyName : null,
                };

                if (isWeekEmpty(workerData.dayDetails)) {
                    // prevent dispatch
                    console.warn('Nothing to submit: all days have 0 hours and no bonuses.');
                    continue;
                }

                // check if the employee has 0 hours and no bonuses, if True, do not create the timesheet and pass to the next one

                // Handle online/offline submission
                if (isOnline) {
                    await dispatch(createTimesheet(timesheetData)).unwrap();
                    successCount++;
                } else {
                    await dispatch(addOfflineTimesheetOperation(timesheetData));
                    successCount++;
                }
            }

            // Save or update team members in the store based on the worksite
            if (formCommon.worksite) {
                const existingTeam = teamMembers[formCommon.worksite.matricule] || [];

                // If there is no team, we add the new workers to the team
                if (existingTeam.length === 0) {
                    dispatch(setTeamForSite({
                        siteMatricule: formCommon.worksite.matricule,
                        team: selectedWorkers
                    }));
                    toast.success('Liste des équipes mise à jour');
                } else {
                    // If a team exists, update it with the current selection
                    const newWorkers = selectedWorkers.filter(worker => !existingTeam.some(existingWorker => existingWorker.matricule === worker.matricule));
                    if (newWorkers.length > 0) {
                        dispatch(setTeamForSite({
                            siteMatricule: formCommon.worksite.matricule,
                            team: [...existingTeam, ...newWorkers]
                        }));
                        toast.success('Liste des équipes mise à jour');
                    }
                }
            }

            if (successCount > 0) {
                if (isOnline) {
                    toast.success(`${successCount} feuilles de temps créées avec succès`);
                } else {
                    toast.success(`${successCount} feuilles de temps sauvegardées localement (mode hors ligne)`);
                }
            }

            // Reset form after successful submission
            setFormCommon({
                chefChantier: null,
                chefProjet: null,
                weekNumber: getCurrentWeekNumber(new Date()),
                timesheetDate: toLocalIsoDate(new Date()),
                worksite: null,
                remark: null
            });
            setSelectedWorkers([]);
            setWorkerSettings({});
            setCurrentWorker(null);
            setIsLoading(false);

            navigation('/timesheetList');
        } catch (error) {
            toast.error('Erreur lors de l\'enregistrement');
            console.error('Error submitting data:', error);
            setIsLoading(false);
        }
    };

    const handleResetDayHours = (worker: Employee, day: DayOfWeek) => {
        // Set hours to 0 for this specific day
        setWorkerSettings({
            ...workerSettings,
            [worker.matricule]: {
                ...workerSettings[worker.matricule],
                dayDetails: {
                    ...workerSettings[worker.matricule].dayDetails,
                    [day]: {
                        ...workerSettings[worker.matricule].dayDetails[day],
                        hours: 0
                    }
                }
            }
        });
    };

    // Function to render day settings for a worker
    const renderWorkerDaySettings = (worker: Employee) => (
        weekdays.map((day) => (
            <Grid item xs={12} key={day.key} >
                <Accordion
                    expanded={expandedDay === day.key}
                    onChange={handleAccordionChange(day.key)}
                    sx={{
                        mb: 1,
                        ...(expandedDay === day.key ? {
                            borderLeft: '4px solid',
                            borderColor: 'primary.main',
                            backgroundColor: 'rgba(0, 0, 0, 0.02)',
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.09)',
                            transition: 'all 0.3s ease-in-out',
                        } : {
                            border: '1px solid',
                            borderColor: 'rgba(0, 0, 0, 0.2)',
                            backgroundColor: 'rgba(0, 0, 0, 0.02)',
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.09)',
                            transition: 'all 0.3s ease-in-out',
                        })
                    }}
                >
                    <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        aria-controls={`${day.key}-content`}
                        id={`${day.key}-header`}
                        sx={{ cursor: 'pointer', padding: '8px 16px', }}
                    >
                        <Grid container alignItems="center" spacing={2} >
                            <Grid item xs={4} sm={12} md={2}>
                                <Typography>{day.label}  {formCommon.timesheetDate &&
                                    format(
                                        addDays(
                                            startOfWeek(new Date(formCommon.timesheetDate), { locale: fr }),
                                            ['lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi', 'dimanche'].indexOf(day.label.toLocaleLowerCase())
                                        ),
                                        'dd MMM',
                                        { locale: fr }
                                    )
                                }</Typography>
                            </Grid>
                            <Grid item xs={8} sm={12} md={6}>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <TextField
                                        select
                                        size="small"
                                        fullWidth
                                        label="Heures"
                                        value={workerSettings[worker.matricule]?.dayDetails[day.key as DayOfWeek]?.hours || 0}
                                        onChange={(e) => handleWorkerHoursChange(worker, day.key as DayOfWeek, Number(e.target.value))}
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        {hoursOptions.map((hours) => (
                                            <MenuItem key={hours} value={hours}>{hours}</MenuItem>
                                        ))}
                                    </TextField>
                                    {workerSettings[worker.matricule]?.dayDetails[day.key as DayOfWeek]?.hours > 0 && <IconButton
                                        size="small"
                                        onClick={(e) => {
                                            e.stopPropagation(); // Prevent accordion expansion when clicking the reset button
                                            handleResetDayHours(worker, day.key as DayOfWeek);
                                        }}
                                        sx={{ ml: 0.5 }}
                                        title="Remise à zéro"
                                    >
                                        <Close fontSize="small" />
                                    </IconButton>}
                                </Box>
                            </Grid>
                            <Grid item xs={12} sm={12} md={4}>
                                <Button
                                    size="medium"
                                    variant="contained"
                                    color="success"
                                    onClick={(e) => {
                                        e.stopPropagation(); // Prevent accordion expansion when clicking the button
                                        handleOpenBonusDialog(worker, day.key as DayOfWeek);
                                    }}
                                    sx={{
                                        ml: 0.5,
                                        textTransform: 'none',
                                    }}
                                    title="Gérer les codes salaires"
                                    startIcon={<StarOutline />}
                                >
                                    Autres codes salaires
                                    {workerSettings[worker.matricule]?.dayDetails[day.key as DayOfWeek]?.bonuses && (
                                        <Chip
                                            size="medium"
                                            label={workerSettings[worker.matricule]?.dayDetails[day.key as DayOfWeek]?.bonuses?.length}
                                            color={workerSettings[worker.matricule]?.dayDetails[day.key as DayOfWeek]?.bonuses?.length! > 0 ? "warning" : "success"}
                                            sx={{ ml: 1, height: 19, }}
                                        />
                                    )}
                                </Button>
                            </Grid>
                            <Grid item xs={12} sm={12} md={12}>
                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                    {workerSettings[worker.matricule]?.dayDetails[day.key as DayOfWeek]?.isDriver && (
                                        <Chip
                                            size="small"
                                            icon={<DriverIcon />}
                                            label="Chauffeur"
                                            color="primary"
                                            variant='outlined'
                                            sx={{ px: 0.5 }}
                                        />
                                    )}
                                    {workerSettings[worker.matricule]?.dayDetails[day.key as DayOfWeek]?.isRecovery && (
                                        <Chip
                                            size="small"
                                            icon={<RecoveryIcon />}
                                            label="récupération"
                                            color="secondary"
                                            variant='outlined'
                                            sx={{ px: 0.5 }}
                                        />
                                    )}
                                    {workerSettings[worker.matricule]?.dayDetails[day.key as DayOfWeek]?.quarters > 0 && (
                                        <Chip
                                            size="small"
                                            label={`+${workerSettings[worker.matricule]?.dayDetails[day.key as DayOfWeek]?.quarters}`}
                                            color="default"
                                            variant='outlined'
                                        />
                                    )}
                                </Box>
                            </Grid>
                        </Grid>
                    </AccordionSummary>
                    <AccordionDetails sx={{ backgroundColor: 'rgba(0, 0, 0, 0.04)', pt: 3 }}>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    select
                                    fullWidth
                                    label="Quart"
                                    value={workerSettings[worker.matricule]?.dayDetails[day.key as DayOfWeek]?.quarters || 0}
                                    onChange={(e) => handleWorkerQuarterChange(worker, day.key as DayOfWeek, Number(e.target.value))}
                                >
                                    {quarterOptions.map((quarter) => (
                                        <MenuItem key={quarter} value={quarter}>
                                            {quarter === 0 ? '0' : `+${quarter}`}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                            <Grid item xs={12} sm={4} display={"flex"} alignItems={"center"}>
                                <Button
                                    variant={workerSettings[worker.matricule]?.dayDetails[day.key as DayOfWeek]?.isDriver ? "contained" : "outlined"}
                                    color="primary"
                                    startIcon={<DriverIcon />}
                                    onClick={(e) => {
                                        const currentValue = Boolean(workerSettings[worker.matricule]?.dayDetails[day.key as DayOfWeek]?.isDriver);
                                        handleWorkerDriverChange(worker, day.key as DayOfWeek, !currentValue);
                                    }}
                                    fullWidth
                                    sx={{
                                        borderRadius: '8px',
                                        textTransform: 'none',
                                        py: 1
                                    }}
                                >
                                    Chauffeur
                                </Button>
                            </Grid>
                            <Grid item xs={12} sm={4} display={"flex"} alignItems={"center"}>
                                <Button
                                    variant={workerSettings[worker.matricule]?.dayDetails[day.key as DayOfWeek]?.isRecovery ? "contained" : "outlined"}
                                    color="secondary"
                                    startIcon={<RecoveryIcon />}
                                    onClick={(e) => {
                                        const currentValue = Boolean(workerSettings[worker.matricule]?.dayDetails[day.key as DayOfWeek]?.isRecovery);
                                        handleWorkerRecoveryChange(worker, day.key as DayOfWeek, !currentValue);
                                    }}
                                    fullWidth
                                    sx={{
                                        borderRadius: '8px',
                                        textTransform: 'none',
                                        py: 1
                                    }}
                                >
                                    Récupération
                                </Button>
                            </Grid>
                        </Grid>
                    </AccordionDetails>
                </Accordion>
            </Grid>
        ))
    );

    const handleWorkerRemarkChange = (worker: Employee, value: string) => {
        setWorkerSettings({
            ...workerSettings,
            [worker.matricule]: {
                ...workerSettings[worker.matricule],
                remark: value
            }
        });
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }} style={{ padding: 0 }}>
            <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }} fontWeight={800}>
                <PostAdd />  Création de la Timesheet  {!isOnline && "(Hors ligne)"}
            </Typography>

            {error && <Box sx={{ mt: 4 }}>
                <Alert severity="error">{error}</Alert>
            </Box>}

            <form onSubmit={handleSubmit}>
                <Grid container spacing={3} >
                    {/* Common form fields for all workers */}
                    <Grid item xs={12}>
                        <Paper sx={{ p: 3, mb: 0, borderRadius: "15px" }} elevation={3}>
                            <Grid container spacing={3}>
                                <Grid item xs={6} md={6}>
                                    <LocalizationProvider dateAdapter={AdapterLuxon} adapterLocale="fr">
                                        <DatePicker
                                            label="Date"
                                            format="dd/MM/yyyy"
                                            value={
                                                formCommon.timesheetDate
                                                    ? DateTime.fromISO(formCommon.timesheetDate) // convert ISO string to Luxon DateTime
                                                    : null
                                            }
                                            onChange={(newValue) => {
                                                if (!newValue) {
                                                    setFormCommon({
                                                        ...formCommon,
                                                        timesheetDate: '',
                                                        weekNumber: 0,
                                                    });
                                                } else {
                                                    // keep ISO string internally
                                                    const iso = newValue.toISODate(); // yields yyyy-MM-dd
                                                    setFormCommon({
                                                        ...formCommon,
                                                        timesheetDate: iso!,
                                                        weekNumber: getISOWeek(newValue.toJSDate()),
                                                    });
                                                }
                                            }}
                                            slotProps={{
                                                textField: {
                                                    fullWidth: true,
                                                    required: true,
                                                    error: !formCommon.timesheetDate,
                                                    helperText: !formCommon.timesheetDate ? 'Ce champ est requis' : '',
                                                },
                                            }}
                                        />
                                    </LocalizationProvider>
                                </Grid>

                                <Grid item xs={6} md={6}>
                                    <TextField
                                        fullWidth
                                        label="Semaine"
                                        value={formCommon.weekNumber}
                                        InputProps={{
                                            readOnly: true,
                                        }}
                                        disabled
                                        helperText="Automatiquement calculée sur base de la date séléctionnée"
                                    />
                                </Grid>

                                <Grid item xs={12} md={4}>
                                    <Autocomplete
                                        id="chef-projet-autocomplete"
                                        options={data?.foremans || []}
                                        value={formCommon.chefProjet}
                                        onChange={(event, newValue) => {
                                            setFormCommon({ ...formCommon, chefProjet: newValue });
                                        }}
                                        getOptionLabel={(option) => option.name + " - " + option.matricule || ''}
                                        isOptionEqualToValue={(option, value) => option.matricule === value.matricule}
                                        filterOptions={multiFieldFilter}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                required
                                                label="Conducteur de travaux"
                                                error={!formCommon.chefProjet}
                                                helperText={!formCommon.chefProjet ? 'Ce champ est requis' : ''}
                                            />
                                        )}
                                    />
                                </Grid>

                                <Grid item xs={12} md={4}>
                                    <Autocomplete
                                        id="worksite-autocomplete"
                                        options={data?.sites || []}
                                        value={formCommon.worksite}
                                        onChange={(event, newValue) => {
                                            setFormCommon({ ...formCommon, worksite: newValue });
                                        }}
                                        getOptionLabel={(option) => option.name + " - " + option.matricule || ''}
                                        isOptionEqualToValue={(option, value) => option.id === value.id}
                                        filterOptions={multiFieldFilter}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Chantier"
                                                placeholder="Selectionnez un chantier"
                                                error={!formCommon.worksite}
                                                helperText={!formCommon.worksite ? 'Ce champ est requis' : ''}
                                            />
                                        )}
                                    />
                                </Grid>

                                <Grid item xs={12} md={4}>
                                    <Autocomplete
                                        id="chef-chantier-autocomplete"
                                        options={data?.workManagers || []}
                                        value={formCommon.chefChantier}
                                        onChange={(event, newValue) => {
                                            setFormCommon({ ...formCommon, chefChantier: newValue });
                                        }}
                                        getOptionLabel={(option) => option.name + " - " + option.matricule || ''}
                                        isOptionEqualToValue={(option, value) => option.matricule === value.matricule}
                                        filterOptions={multiFieldFilter}
                                        disabled={userRole?.toLowerCase() === "user"} // Disable the field so it can't be changed
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                required
                                                label="Chef d'équipes"
                                                error={!formCommon.chefChantier}
                                                helperText={!formCommon.chefChantier ? 'Ce champ est requis' : ''}
                                            />
                                        )}
                                    />
                                </Grid>
                            </Grid>
                        </Paper>
                    </Grid>

                    {/* Worker Selection */}
                    <Grid item xs={12} md={4} sx={{ mb: 3 }}>
                        <Paper sx={{ p: 3, mb: 0, height: 'calc(100% - 24px)', borderRadius: "15px" }} elevation={3}>
                            <Box sx={{ mb: 2 }}>
                                <Typography variant="body1" gutterBottom>
                                    Séléction des employés
                                </Typography>
                                <FormControl fullWidth required error={selectedWorkers.length === 0}>
                                    <Autocomplete
                                        multiple
                                        id="workers-autocomplete"
                                        options={data?.employees || []}
                                        value={selectedWorkers}
                                        onChange={(event, newValue) => {
                                            setSelectedWorkers(newValue);
                                        }}
                                        getOptionLabel={(option) => option.name + " - " + option.matricule || ''}
                                        filterOptions={multiFieldFilter}
                                        // Add this line to properly compare employee objects
                                        isOptionEqualToValue={(option, value) => option.matricule === value.matricule}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Employés"
                                                placeholder="Chercher un employé..."
                                                error={selectedWorkers.length === 0}
                                            />
                                        )}
                                        renderOption={(props, option, { selected }) => {
                                            // Extract key from props to pass it directly

                                            return (
                                                <li {...props}>
                                                    <Checkbox
                                                        icon={<CheckBoxOutlineBlank fontSize="small" />}
                                                        checkedIcon={<CheckBox fontSize="small" />}
                                                        style={{ marginRight: 8 }}
                                                        checked={Boolean(selected)}
                                                    />
                                                    {option.name + " - " + option.matricule}
                                                    <div style={{ display: "flex", flexDirection: "column", justifyContent: "space-between", alignSelf: "end" }}>

                                                        {option.isInterim && <Chip
                                                            size="small"
                                                            label={'Intérimaire'}
                                                            sx={{ fontSize: '0.75rem', height: '20px', ml: 1, mb: 1 }}
                                                            color="secondary"
                                                            variant="outlined"
                                                            icon={<PersonIcon fontSize="small" />}
                                                        />}
                                                        {option.interimCompanyName && <Chip
                                                            size="small"
                                                            label={option.interimCompanyName}
                                                            sx={{ fontSize: '0.75rem', height: '20px', ml: 1 }}
                                                            color="default"
                                                            variant="outlined"
                                                            icon={<Apartment fontSize="small" />}
                                                        />}
                                                    </div>
                                                </li>
                                            );
                                        }}
                                        renderTags={(value, getTagProps) => (
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                <Typography variant="body2">
                                                    {value.length} Employé(s) séléctionnés
                                                </Typography>
                                            </Box>
                                        )}
                                    />
                                    {selectedWorkers.length === 0 &&
                                        <Typography variant="caption" color="error">
                                            Veuillez selectionner au moins un employé
                                        </Typography>
                                    }
                                </FormControl>
                            </Box>

                            <Divider sx={{ my: 2 }} />

                            <Typography variant="subtitle1" gutterBottom>
                                Employés séléctionnés
                            </Typography>

                            {selectedWorkers.length === 0 ? (
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
                                    Aucun employé séléctionné
                                </Typography>
                            ) : (
                                <List sx={{ maxHeight: '60vh', overflow: 'auto' }}>
                                    {selectedWorkers.map((worker) => (
                                        <ListItem
                                            key={worker.matricule}
                                            disablePadding
                                            secondaryAction={
                                                <IconButton
                                                    edge="end"
                                                    aria-label="remove"
                                                    onClick={() => handleRemoveWorker(worker)}
                                                >
                                                    &times;
                                                </IconButton>
                                            }
                                            sx={{ mb: 1 }}
                                        >
                                            <ListItemButton
                                                selected={currentWorker === worker}
                                                onClick={() => handleSelectWorker(worker)}
                                                sx={{ borderRadius: 1 }}
                                            >
                                                <Avatar
                                                    sx={{
                                                        mr: 1,
                                                        bgcolor: 'primary.main'
                                                    }}
                                                >
                                                    <PersonIcon />
                                                </Avatar>
                                                <ListItemText
                                                    primary={
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            {worker.name + " - " + worker.matricule}
                                                            {workerSettings[worker.matricule]?.isWeekDriver && (
                                                                <Tooltip title="Chauffeur en semaine">
                                                                    <WeekDriverIcon color="primary" fontSize="small" />
                                                                </Tooltip>
                                                            )}
                                                            {workerSettings[worker.matricule]?.isWeekRecovery && (
                                                                <Tooltip title="Semaine de récupération">
                                                                    <RecoveryIcon color="primary" fontSize="small" />
                                                                </Tooltip>
                                                            )}
                                                            <div style={{ display: "flex", flexDirection: "column", justifyContent: "space-around" }}>

                                                                {worker.isInterim && <Chip
                                                                    size="small"
                                                                    label={'Intérimaire'}
                                                                    sx={{ fontSize: '0.75rem', height: '20px', px: 0.5, mb: 1 }}
                                                                    color="secondary"
                                                                    variant="outlined"
                                                                    icon={<PersonIcon fontSize="small" />}
                                                                />}

                                                                {worker.interimCompanyName && <Chip
                                                                    size="small"
                                                                    label={worker.interimCompanyName}
                                                                    sx={{ fontSize: '0.75rem', height: '20px', px: 0.5 }}
                                                                    color="default"
                                                                    variant="outlined"
                                                                    icon={<Apartment fontSize="small" />}
                                                                />}
                                                            </div>
                                                        </Box>
                                                    }
                                                    secondary={
                                                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                                                            {worker.role || 'Pas de rôle assigné'}
                                                        </Typography>
                                                    }
                                                />
                                            </ListItemButton>
                                        </ListItem>
                                    ))}
                                </List>
                            )}
                        </Paper>
                    </Grid>

                    {/* Worker Settings */}
                    <Grid item xs={12} md={8} >
                        <Paper sx={{ p: 3, height: '100%', borderRadius: "15px" }} elevation={3}>
                            {currentWorker ? (
                                <Box>
                                    {/* Worker Settings Header */}
                                    <Box sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        mb: 2,
                                        flexWrap: 'wrap',
                                        gap: 2
                                    }}>
                                        <Typography variant="body1">
                                            Paramètres de {currentWorker.name}
                                        </Typography>

                                        <Box sx={{ display: 'flex', gap: 2 }}>
                                            <Button
                                                color="error"
                                                variant="contained"
                                                size="small"
                                                startIcon={<ZeroHoursIcon />}
                                                onClick={() => handleResetWorkerHoursToZero(currentWorker)}
                                            >
                                                remettre les heures a zéro
                                            </Button>
                                        </Box>
                                    </Box>

                                    {/* Worker Specific Settings */}
                                    <Box sx={{ bgcolor: 'action.hover', p: 2, borderRadius: 1, mb: 2 }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                                            <Typography variant="body2">
                                                Paramètres spécifiques a l'employé séléctioné.
                                            </Typography>

                                            <Grid item xs={12}>
                                                <FormControlLabel
                                                    control={
                                                        <Switch
                                                            checked={WEEKDAYS.every(day => workerSettings[currentWorker.matricule]?.isWeekRecovery)}
                                                            onChange={(e) => handleWeekRecoveryChange(currentWorker, e.target.checked)}
                                                            color="primary"
                                                        />
                                                    }
                                                    label={
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <RecoveryIcon color="primary" />
                                                            <Typography variant="body2">Semaine de récupération</Typography>
                                                        </Box>
                                                    } />
                                            </Grid>

                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        checked={workerSettings[currentWorker.matricule]?.isWeekDriver || false}
                                                        onChange={(e) => handleWorkerWeekDriver(currentWorker, e.target.checked)}
                                                        color="primary"
                                                    />
                                                }
                                                label={
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <WeekDriverIcon color="primary" />
                                                        <Typography variant="body2">Chauffeur toute la semaine</Typography>
                                                    </Box>
                                                }
                                            />
                                        </Box>
                                        <Box sx={{ mb: 3, mt: 2 }}>
                                            <TextField
                                                fullWidth
                                                label="Remarques"
                                                placeholder="Add notes specific to this worker's timesheet"
                                                multiline
                                                rows={2}
                                                value={workerSettings[currentWorker.matricule]?.remark || ''}
                                                onChange={(e) => handleWorkerRemarkChange(currentWorker, e.target.value)}
                                            />
                                        </Box>
                                        {/* Hours Summary Section */}
                                        <Divider sx={{ my: 2 }} />
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 2, justifyContent: 'space-between' }}>
                                            <Chip
                                                label={`Total des heures: ${WEEKDAYS.reduce((sum, day) =>
                                                    sum + ((workerSettings[currentWorker.matricule]?.dayDetails[day]?.hours+ workerSettings[currentWorker.matricule]?.dayDetails[day]?.quarters) || 0), 0)}`}
                                                color="primary"
                                                icon={<Work />}
                                                variant="outlined"
                                                sx={{ px: 1 }}
                                            />
                                        </Box>
                                    </Box>

                                    {/* Worker Days Settings */}
                                    <Grid container spacing={2} style={{ padding: 0 }}>
                                        {renderWorkerDaySettings(currentWorker)}
                                    </Grid>
                                </Box>
                            ) : (
                                <Box sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    height: '100%',
                                    p: 3
                                }}>
                                    <Typography variant="body1" color="text.secondary" gutterBottom>
                                        Séléctionnez un employé
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" align="center">
                                        Cliquez sur un employé pour voir le détail
                                    </Typography>
                                </Box>
                            )}
                        </Paper>
                    </Grid>

                    {/* Submit button */}
                    <Grid item xs={12}>
                        <Paper sx={{ p: 3, mt: 6, borderRadius: "15px" }} elevation={3}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                                <Typography>
                                    {selectedWorkers.length} Employé(s) séléctionnés
                                </Typography>
                                <Button
                                    type="submit"
                                    variant="contained"
                                    color="primary"
                                    size="large"
                                    disabled={!isFormValid || selectedWorkers.length === 0 || isloading}
                                >
                                    {isOnline
                                        ? `Créer ${selectedWorkers.length} feuille(s) de temps(s)`
                                        : `Créer ${selectedWorkers.length} feuille(s) de temps(s) (Offline Mode)`}
                                </Button>
                            </Box>
                        </Paper>
                    </Grid>
                </Grid>
            </form>


            <BonusDialog
                open={bonusDialogOpen}
                onClose={() => setBonusDialogOpen(false)}
                day={currentBonusDay.day || ''}
                dayLabel={currentBonusDay.day ?
                    weekdays.find(d => d.key === currentBonusDay.day)?.label || ''
                    : ''}
                worker={currentBonusDay.worker?.name || ''}
                bonuses={currentBonusDay.worker && currentBonusDay.day
                    ? (workerSettings[currentBonusDay.worker.matricule]?.dayDetails[currentBonusDay.day]?.bonuses || [])
                    : []
                }
                salaryCodes={data?.salaries || []}
                onSave={handleSaveBonuses}
                timesheetDate={formCommon.timesheetDate}
            />
        </Container>
    );
};